import { ApolloError, useApolloClient } from '@apollo/client';
import {
  BuildCircle,
  FileCopyOutlined,
  ForwardToInbox,
  History,
  ImportContacts,
  MobileScreenShare,
  Search,
  Spellcheck,
  Visibility,
  Whatshot,
} from '@mui/icons-material';
import { Backdrop, CircularProgress, Menu, MenuItem } from '@mui/material';
import {
  BackTo,
  EditAppBar,
  NotificationTypeEnum,
  PageWithDrawer,
  useConcurrentialAccess,
  useDialog,
  useNotification,
} from '@prismamedia/one-components';
import { EditorState, convertToRaw } from 'draft-js';
import { isEmpty } from 'lodash';
import { editorStateFromRaw } from 'megadraft';
import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { useBeforeUnload, useKey } from 'react-use';
import {
  ArticleFormat,
  ArticleLikeInput,
  ArticleStatus,
  BrandKey,
  GetArticleQuery_query_article_Article,
} from '../../__generated__/queries-web';
import { useGetArticleHistoryCount } from '../../apollo/queries/articleHistoryCount.web.graphql';
import { GET_ARTICLE_URL_STATUS } from '../../apollo/queries/articles.web.graphql';
import { useGetBrandKey } from '../../apollo/sharedQueries/getBrandKeys.web.graphql';
import { ArticlesRelatedMediaDialog } from '../../components/ArticlesRelatedMediaDialog';
import { ProlexisDialog } from '../../components/ProlexisDialog';
import { InputText } from '../../components/ProlexisDialog/types';
import { SaveButton } from '../../components/SaveButton';
import { StatusSelector } from '../../components/StatusSelector';
import { Tags } from '../../components/Tags';
import { paths } from '../../routing/Router/paths';
import { auth } from '../../utils/auth';
import { getBrandOptions, getTitleFromBrandKey } from '../../utils/brands';
import { useThunkReducer } from '../../utils/thunkReducer';
import { formatPreviewUrl } from '../../utils/url';
import { useDailymotionThumbnail } from '../../utils/useDailymotionThumbnail';
import { useGetPushCount } from '../MobilePush/MobilePushList/getPushByBrankey.web.graphql';
import { ArticleHistory } from '../Pages/components/AppBar/Edit/ArticleHistory';
import { ArticleEditor } from './ArticleEditor';
import { CategoriesInput } from './ArticleEditor/CategoriesInput';
import { SourceInput } from './ArticleEditor/SourceInput';
import { ArticleToolBar } from './ArticleToolBar';
import { DrawerContent } from './DrawerContent';
import { Qualifiers } from './DrawerContent/Qualifiers';
import { DuplicatePrintDialog } from './DuplicatePrintDialog';
import { NLFlashDialog } from './NLFlashDialog';
import { useStatusTransition } from './StatusTransition/useStatusTransition';
import { ARTICLE_ENHANCEMENT_MAIN_TITLE } from './constants';
import { useGetAssigment } from './queries/getAssigment.topic.graphql';
import {
  ArticleEditAction,
  ArticleState,
  actions,
  initState,
  reducer,
} from './reducer';
import { requiredVideoFields } from './saveMandatoryFields';
import { useStyles } from './styles';
import { useSaveArticle } from './useSaveArticle';
import { useArticleConcurrentialAccess } from './utils/articleConcurrentialAccess';
import { formatSubTitle } from './utils/formatSubtitle';
import { renderInputFromTemplate } from './utils/renderFromTemplate';
import { getTagsConfiguration } from './utils/tagsConfiguration';
import { isPushActionStatus } from '../../utils/statuses';
import { PublicationStatusDialog } from '../../components/PublicationStatusDialog';
import ReactGA from 'react-ga4';
import { StringParam, useQueryParam } from 'use-query-params';

interface ArticleEditProps {
  data: GetArticleQuery_query_article_Article;
  subjectId?: string;
  subjectTitle?: string;
  error?: Error | ApolloError;
  loading?: boolean;
  disableConcurrentialAccess?: boolean;
}

interface ArticleEditParams {
  brandKey: BrandKey;
  articleType: string;
  id: string;
  search: string;
}

export const ArticleEdit: FC<ArticleEditProps> = ({
  data,
  subjectId,
  subjectTitle,
  error,
  loading,
  disableConcurrentialAccess = false,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { articleType, id } = useParams<ArticleEditParams>();
  const { openDialog, closeDialog } = useDialog();
  const { pushNotification } = useNotification();
  const client = useApolloClient();
  const {
    data: articleHistoryCountData,
    loading: historyLoading,
  } = useGetArticleHistoryCount(id);
  const [state, dispatch] = useThunkReducer<
    ArticleState,
    ArticleEditAction,
    GetArticleQuery_query_article_Article
  >(reducer, data, initState);

  const renderInput = useMemo(
    () => renderInputFromTemplate(state.format, state.brandKey || BrandKey.CAP),
    [state.format, state.brandKey],
  );

  const tagsConfiguration = useMemo(
    () =>
      getTagsConfiguration({
        events: state.events,
        guides: renderInput('guides') ? state.guides : undefined,
        tags: state.tags,
        brandKey: state.brandKey,
      }),
    [state.tags, state.events, state.brandKey, state.guides, renderInput],
  );

  const liveAndPushRef = useRef(null);
  const drawerCenterRef = useRef(null);
  const drawerButtonRef = useRef(null);
  const wrapperRef = useRef(null);

  const {
    lockArticle,
    unlockArticle,
    retainArticleLock,
  } = useArticleConcurrentialAccess();

  useConcurrentialAccess({
    lockStatus: { lockedAt: data.lockedAt, lockedBy: data.lockedBy },
    unlockTimeout: state.articleChanged ? 1800000 : 600000,
    currentUser: auth.user,
    onLock: (firstLock) =>
      disableConcurrentialAccess
        ? Promise.resolve()
        : firstLock
        ? lockArticle(data.id)
        : retainArticleLock(data.id),
    onNavigateBack: () =>
      history.push(isVideoPage ? paths.ARTICLE_VIDEO : paths.ARTICLE_LIST),
    disableLockForV0: true,
  });

  const machineTagData = data.machineTags?.find((_machineTagdata) =>
    _machineTagdata.machineTag.tag.startsWith('topicassignment:id'),
  );
  const subject = useGetAssigment(machineTagData?.machineTag.id || '').data
    ?.assignment?.subject;
  if (subject) {
    // eslint-disable-next-line no-param-reassign
    subjectId = subject.id;
    // eslint-disable-next-line no-param-reassign
    subjectTitle = subject.title;
  }

  useEffect(() => {
    dispatch(actions.initArticleData({ articleData: data }));
  }, [data, dispatch]);

  const { saveArticle, loading: saveLoading } = useSaveArticle(state, dispatch);

  useStatusTransition({
    currentStatus: state.status,
    newStatus: state.newStatus,
    transitionType: state.transitionType,
    dispatch,
    saveArticle,
    publishedAt: state.publishedAt,
  });

  useBeforeUnload(
    state.articleChanged,
    'Vous avez des changements non sauvegardés, êtes-vous sûr de vouloir quitter ?',
  );

  useKey(
    (event: KeyboardEvent) => {
      if (event.key === 's' && event.metaKey) {
        event.preventDefault();
        if (!saveLoading) {
          return true;
        }
      }
      return false;
    },
    () => saveArticle(),
  );

  const lastHistoryDate = state.histories?.[0]?.createdAt;
  const lastModifiedDate =
    lastHistoryDate && state.updatedAt && lastHistoryDate >= state.updatedAt
      ? lastHistoryDate
      : state.updatedAt;
  const subTitleText = formatSubTitle(
    state.status,
    state.publishedAt,
    lastModifiedDate,
  );
  const pushCount = useGetPushCount(state.id);

  const lead = isEmpty(state.lead) ? null : state.lead;
  const body = isEmpty(state.body) ? null : state.body;
  const isVideoPage = state.format === ArticleFormat.Video;
  const isNewVideoArticle =
    state.format === ArticleFormat.Video &&
    state.status === ArticleStatus.Draft;
  const publicUrl = formatPreviewUrl(state.canonical || null, state.brandKey);
  const displayDuplicateAction =
    state.format !== ArticleFormat.Video &&
    state.status !== ArticleStatus.Draft;
  const displayNLFlashAction =
    state.format !== ArticleFormat.Video &&
    state.status === ArticleStatus.Published &&
    getBrandOptions(state.brandKey).enableNLFlash;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showBlockCounters, setShowBlockCounters] = useState(true);

  const { data: brandKeys } = useGetBrandKey();

  useDailymotionThumbnail(state.medias || [], isVideoPage);

  // URL param to control NL Flash dialog
  const [nlFlash, setNlFlash] = useQueryParam('nlFlash', StringParam);

  useEffect(() => {
    const shouldOpen =
      typeof nlFlash === 'string' && nlFlash.toLowerCase() === 'true';
    if (displayNLFlashAction && shouldOpen) {
      openDialog(
        <NLFlashDialog
          article={state}
          onClose={() => setNlFlash(undefined, 'replaceIn')}
        />,
      );
    }
  }, [nlFlash, displayNLFlashAction, openDialog, setNlFlash, state]);

  const handleClick = (brandKey: string) => {
    setAnchorEl(null);
    const newLocation = generatePath(paths.ARTICLE_DUPLICATE, {
      id,
      brandKey,
    });
    window.open(`${window.location.origin}#${newLocation}`);
  };

  const handleTagsChange = useCallback(
    (tags, field) => {
      dispatch(
        actions.setArticleTags({
          field,
          value: tags,
        }),
      );
    },
    [dispatch],
  );

  const closeProlexis = (texts: InputText[] | false) => {
    if (texts !== false) {
      texts.forEach(({ name, value }) => {
        if (name === 'title') {
          dispatch(
            actions.updateField({
              field: name,
              value,
            }),
          );
        }
        try {
          if (value) {
            const val = value as EditorState;
            const content = val?.getCurrentContent();
            if (content) {
              const rawContent = convertToRaw(content);
              dispatch(
                actions.updateField({
                  field: name,
                  value: rawContent,
                }),
              );
            }
          }
        } catch (e) {
          closeDialog();
        }
      });
    }
    closeDialog();
  };

  const historyAction =
    state.histories?.length > 0
      ? [
          {
            id: 'history',
            label: 'Historique',
            icon: <History />,
            onClick: () => {
              openDialog(
                <ArticleHistory
                  histories={state.histories}
                  historyCount={articleHistoryCountData?.articleHistoryCount}
                  loading={historyLoading}
                />,
                {
                  maxWidth: 'lg',
                },
              );
            },
          },
        ]
      : [];

  const prolexisAction = [
    {
      id: 'correction',
      label: 'Correction',
      icon: <Spellcheck />,
      onClick: () => {
        ReactGA.event({
          category: 'One Web Prolexis',
          action: 'One Web Prolexis Correction',
        });
        openDialog(
          <ProlexisDialog
            texts={[
              {
                label: 'Corps',
                name: 'body',
                type: 'DRAFT',
                value: editorStateFromRaw(state.body || ''),
              },
              {
                label: 'Titre',
                name: 'title',
                type: 'TEXT',
                value: state.title || '',
              },
              {
                label: 'Chapô',
                name: 'lead',
                type: 'DRAFT',
                value: editorStateFromRaw(state.lead || ''),
              },
            ]}
            handleClose={closeProlexis}
            brandKey={state.brandKey}
          />,
          {
            maxWidth: 'lg',
          },
        );
      },
    },
  ];

  const locationAction = [
    {
      id: 'location',
      label: 'Localisation',
      icon: <Search />,
      onClick: () => {
        const idMedia = state.medias?.[0]?.iframely?.meta?.public_id;

        if (!idMedia) {
          pushNotification({
            type: NotificationTypeEnum.error,
            message: "L'identifiant du media n'existe pas",
          });
          return;
        }

        openDialog(
          <ArticlesRelatedMediaDialog
            idMedia={idMedia}
            title={state.title}
            articleFormat={state.format}
            handleClose={closeDialog}
          />,
          { maxWidth: 'lg' },
        );
      },
    },
  ];

  const videosActions = [...locationAction, ...historyAction];

  const showButtonSeeFront =
    state.status === 'Scheduled' || state.status === 'Published';

  const handlePreviewClick = useCallback(async () => {
    if (!publicUrl) return;

    if (config.ENV === 'REC2') {
      window.open(publicUrl);
      return;
    }

    try {
      const result = await client.query({
        query: GET_ARTICLE_URL_STATUS,
        variables: { where: { id: state.id } },
        fetchPolicy: 'network-only',
      });

      const urlStatus = result?.data?.article?.urls?.[0]?.status?.ok;

      if (urlStatus) {
        window.open(publicUrl);
      } else {
        openDialog(
          <PublicationStatusDialog url={publicUrl} onClose={closeDialog} />,
          {
            maxWidth: 'sm',
          },
        );
      }
    } catch (fetchError) {
      // If there's an error fetching the status, show the dialog
      openDialog(
        <PublicationStatusDialog url={publicUrl} onClose={closeDialog} />,
        {
          maxWidth: 'sm',
        },
      );
    }
  }, [client, state.id, publicUrl, openDialog, closeDialog]);

  const richActions = [
    ...(publicUrl && showButtonSeeFront
      ? [
          {
            id: 'preview',
            label: 'Visualiser',
            icon: <Visibility />,
            onClick: handlePreviewClick,
          },
        ]
      : []),
    ...(isPushActionStatus(state.status) &&
    (getBrandOptions(state.brandKey).pushPlatforms?.length ||
      getBrandOptions(state.brandKey).editorialAlertAllowed)
      ? [
          {
            id: 'push',
            label: 'Push',
            icon: <MobileScreenShare ref={liveAndPushRef} />,
            onClick: () =>
              history.push(`/${state.brandKey}/mobilepush/add?id=${id}`),
            badge: pushCount,
          },
        ]
      : []),
    ...(displayNLFlashAction
      ? [
          {
            id: 'nlFlash',
            label: 'NL Flash',
            icon: <ForwardToInbox />,
            onClick: () => setNlFlash('true', 'pushIn'),
          },
        ]
      : []),
    ...historyAction,
    ...prolexisAction,
    ...(displayDuplicateAction
      ? [
          {
            id: 'duplicate',
            label: 'Duplication web',
            icon: <FileCopyOutlined />,
            onClick: (e: MouseEvent<HTMLButtonElement>) =>
              setAnchorEl(e.currentTarget),
          },
        ]
      : []),
    ...(displayDuplicateAction
      ? [
          {
            id: 'duplicatePrint',
            label: 'Duplication print',
            icon: <ImportContacts />,
            onClick: () =>
              openDialog(<DuplicatePrintDialog articleState={state} />, {
                fullWidth: true,
                maxWidth: 'md',
              }),
          },
        ]
      : []),
    {
      id: 'live',
      label: 'Live',
      icon: <Whatshot ref={liveAndPushRef} />,
      onClick: () =>
        history.push(`/${state.brandKey}/${articleType}/live/${id}`),
      badge: data?.live?.postCount,
    },
    ...(state.status === 'Published'
      ? [
          {
            id: 'diagnostic',
            label: 'Diagnostic',
            icon: <BuildCircle />,
            onClick: () => {
              window.open(
                `${config.FRONT_DIAGNOSTIC}/pipeline/create?manifest=one%2FgetContent&id=${id}&_target=all&_format=notify`,
              );
            },
          },
        ]
      : []),
  ];

  const barActions =
    state.format === ArticleFormat.Video ? videosActions : richActions;

  const handleNavigationBack = useCallback(() => {
    if (data.id) {
      // When lockedBy === null is the case an user go to an unlocked article and lock it
      if (auth.user?.id === data.lockedBy?.id || data.lockedBy === null) {
        unlockArticle(data.id).catch(() => {});
      }
      history.push(isVideoPage ? paths.ARTICLE_VIDEO : paths.ARTICLE_LIST);
    }
  }, [data.id, history, isVideoPage, unlockArticle, data.lockedBy]);

  return (
    <div className={classes.wrapper} ref={wrapperRef}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={saveLoading}
      >
        <CircularProgress color="inherit" size={100} />
      </Backdrop>
      <EditAppBar
        onNavigateBack={handleNavigationBack}
        title={state.title || undefined}
        subTitle={subTitleText}
        actions={barActions}
        save={
          isVideoPage
            ? {
                actionLabel: isNewVideoArticle ? 'Créer la vidéo' : undefined,
                loading: saveLoading,
                onClick: async () => {
                  try {
                    await saveArticle();
                  } catch (e) {}
                },
                requiredFields: isNewVideoArticle
                  ? requiredVideoFields(state)
                  : undefined,
              }
            : undefined
        }
        concurrentialAccess={{
          lockStatus: { lockedBy: data.lockedBy || auth.user },
          disableLockForV0: true,
        }}
        additionalElement={
          <>
            {!isVideoPage && (
              <SaveButton
                loading={saveLoading}
                onClick={saveArticle}
                editedAt={state.editedAt}
              />
            )}
            {!isNewVideoArticle && (
              <StatusSelector
                articleState={state}
                loading={saveLoading}
                currentStatus={state.status}
                changeStatus={(newStatus, transitionType) => {
                  if (state.format === 'Video' && newStatus === 'Deleted') {
                    const idMedia =
                      state.medias?.[0]?.iframely?.meta?.public_id;

                    if (!idMedia) {
                      pushNotification({
                        type: NotificationTypeEnum.error,
                        message: "L'identifiant du media n'existe pas",
                      });
                      return;
                    }

                    openDialog(
                      <ArticlesRelatedMediaDialog
                        idMedia={idMedia}
                        title={state.title}
                        articleFormat={state.format}
                        handleDelete={() => {
                          dispatch(
                            actions.changeStatus({ newStatus, transitionType }),
                          );
                          closeDialog();
                        }}
                        handleClose={closeDialog}
                      />,
                      { maxWidth: 'lg' },
                    );
                  } else {
                    dispatch(
                      actions.changeStatus({ newStatus, transitionType }),
                    );
                  }
                }}
              />
            )}
          </>
        }
        toolbar={
          <ArticleToolBar
            brandLabel={getTitleFromBrandKey(state.brandKey) ?? undefined}
            title={state.title}
            lead={lead}
            body={body}
            showBlockCounters={showBlockCounters}
            onToggleBlockCounters={() =>
              setShowBlockCounters(!showBlockCounters)
            }
          />
        }
      />

      <PageWithDrawer
        error={error}
        loading={loading}
        paddingTop={48}
        fullWidthRight={state.format === ArticleFormat.Rich}
        ref={drawerCenterRef}
        refs={{ rightButton: drawerButtonRef }}
        rightDrawer={
          <DrawerContent
            subjectId={subjectId}
            subjectTitle={subjectTitle}
            lead={lead}
            id={state.id}
            body={body}
            title={state.title}
            publishedAt={state.publishedAt}
            categories={state.categories}
            tags={state.tags}
            authors={state.authors}
            source={state.source}
            events={state.events}
            status={state.status}
            brandKey={state.brandKey}
            slug={state.slug}
            renderInput={renderInput}
            qualifiers={state.qualifiers}
            guides={state.guides}
            channels={state.channels}
            dispatch={dispatch}
            format={state.format}
            machineTags={state.machineTags}
          />
        }
      >
        <ArticleEditor
          id={id || data.id}
          articleEditWrapperRef={wrapperRef}
          body={body}
          brandKey={state.brandKey}
          dispatch={dispatch}
          format={state.format}
          lead={lead}
          medias={state.medias}
          partnerTitle={state.partnerTitle}
          discoverTitle={state.discoverTitle}
          renderInput={renderInput}
          secondaryTitle={state.secondaryTitle}
          subjectId={subjectId}
          title={state.title}
          platformId={state.platformId}
          status={state.status}
          publicUrl={publicUrl}
          authors={state.authors}
          slug={state.slug}
          urls={state.urls}
          publishedAt={state.publishedAt}
          showBlockCounters={showBlockCounters}
          SourceComponent={
            renderInput('source') && (
              <SourceInput
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                brandKey={state.brandKey!}
                source={state.source}
                dispatch={dispatch}
              />
            )
          }
          CategoriesComponent={
            (renderInput('firstCategory') || renderInput('secondCategory')) && (
              <CategoriesInput
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                brandKey={state.brandKey!}
                categories={state.categories}
                dispatch={dispatch}
                multiple={
                  renderInput('firstCategory') && renderInput('secondCategory')
                }
              />
            )
          }
        />

        <Tags
          article={
            {
              body: state.body,
              lead: state.lead,
              id: [state.id],
              title: state.title,
              format: state.format,
            } as ArticleLikeInput
          }
          brandKey={state.brandKey}
          configuration={tagsConfiguration}
          onTagsChange={handleTagsChange}
          title={ARTICLE_ENHANCEMENT_MAIN_TITLE}
          QualifierComponent={
            <Qualifiers
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              brandKey={state.brandKey!}
              qualifiers={state.qualifiers}
              articleFormat={state.format}
              onChange={(updatedQualifiers) => {
                dispatch(
                  actions.updateField({
                    field: 'qualifiers',
                    value: updatedQualifiers,
                  }),
                );
              }}
              label={'Qualifiers'}
            />
          }
        />

        <BackTo
          className={classes.backToButton}
          elementToScrollRef={drawerCenterRef}
        />
      </PageWithDrawer>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {brandKeys &&
          brandKeys.categories &&
          brandKeys.categories.map(({ brandKey }) => (
            <MenuItem key={brandKey} onClick={() => handleClick(brandKey)}>
              {brandKey}
            </MenuItem>
          ))}
      </Menu>
    </div>
  );
};
